import * as React from "react";
import { LocalStorageKeys } from "src/localstorage/keys";
import {
  CreateGameArgs,
  JoinToGameArgs,
  AddVoteArgs,
  ReconnectToGameArgs,
  TaskFinalResultArgs,
  TimerArgs,
  AuthGameId,
  UpdateUserArgs,
  UpdateTaskArgs,
  NewTaskArgs,
  RemoveTaskArgs,
  NewVotingArgs,
} from "src/socket/socket.args";
import {
  createGameEmitter,
  createTimerEmitter,
  finalResultEmitter,
  finishGameEmitter,
  joinGameEmitter,
  castVoteEmitter,
  reconnectToGameEmitter,
  addTimeEmitter,
  cancelTimerEmitter,
  resetTimerEmitter,
  updateUserEmitter,
  revealVotesEmitter,
  startNewVotingEmitter,
  systemsEmitter,
  updateTaskEmitter,
  createTaskEmitter,
  removeTaskEmitter,
} from "src/socket/socket.emitters";
import {
  registerListeners,
  deregisterListeners,
} from "src/socket/socket.listeners";
import { CreateGameResponse } from "src/socket/socket.responses";
import { useSocketContext } from "src/socket/useSocketContext";
import { GameContext } from "./game.context";
import { gameReducer } from "./game.reducer";
import { ActionTypes } from "./game.reducer.types";

export const GameContextProvider: React.FunctionComponent = ({ children }) => {
  const socket = useSocketContext();
  const [gameState, dispatch] = React.useReducer(gameReducer, {
    token: null,
    votingFinished: false,
  });

  const createGame = React.useCallback(
    async (data: CreateGameArgs): Promise<CreateGameResponse> => {
      const response = await createGameEmitter(socket, data);
      dispatch({ type: ActionTypes.CREATE, data: response });
      return response;
    },
    [socket]
  );

  const joinGame = React.useCallback(
    async (data: JoinToGameArgs) => {
      const response = await joinGameEmitter(socket, data);
      dispatch({ type: ActionTypes.JOIN, data: response });
      return response;
    },
    [socket]
  );

  const castVote = React.useCallback(
    async (data: AddVoteArgs) => {
      return await castVoteEmitter(socket, data);
    },
    [socket]
  );

  const reconnectToGame = React.useCallback(
    async (data: ReconnectToGameArgs) => {
      const response = await reconnectToGameEmitter(socket, data);
      dispatch({
        type: ActionTypes.JOIN,
        data: { ...response, data: { ...response.data, token: data.token } },
      });
      return response;
    },
    [socket]
  );

  const createTimer = React.useCallback(
    async (data: TimerArgs) => {
      return await createTimerEmitter(socket, data);
    },
    [socket]
  );

  const addTime = React.useCallback(
    async (data: TimerArgs) => {
      return await addTimeEmitter(socket, data);
    },
    [socket]
  );

  const cancelTime = React.useCallback(
    async (data: AuthGameId) => {
      return await cancelTimerEmitter(socket, data);
    },
    [socket]
  );

  const resetTimer = React.useCallback(
    async (data: AuthGameId) => {
      return await resetTimerEmitter(socket, data);
    },
    [socket]
  );

  const updateUser = React.useCallback(
    async (data: UpdateUserArgs) => {
      return await updateUserEmitter(socket, data);
    },
    [socket]
  );

  const revealVotes = React.useCallback(
    async (data: AuthGameId) => {
      return await revealVotesEmitter(socket, data);
    },
    [socket]
  );

  const startNewVoting = React.useCallback(
    async (data: NewVotingArgs) => {
      return await startNewVotingEmitter(socket, data);
    },
    [socket]
  );

  const getVotingSystems = React.useCallback(async () => {
    const response = await systemsEmitter(socket);
    dispatch({ type: ActionTypes.GET_SYSTEMS, data: response });
    return response;
  }, [socket]);

  const getLocalToken = React.useCallback(() => {
    const token = localStorage.getItem(LocalStorageKeys.gameTokenKey);
    dispatch({ type: ActionTypes.SET_TOKEN, data: { token } });
    return token;
  }, []);

  const finishGame = React.useCallback(
    async (data: AuthGameId) => {
      return await finishGameEmitter(socket, data);
    },
    [socket]
  );

  const updateTask = React.useCallback(
    async (data: UpdateTaskArgs) => {
      return await updateTaskEmitter(socket, data);
    },
    [socket]
  );

  const addTask = React.useCallback(
    async (data: NewTaskArgs) => {
      return await createTaskEmitter(socket, data);
    },
    [socket]
  );

  const removeTask = React.useCallback(
    async (data: RemoveTaskArgs) => {
      return await removeTaskEmitter(socket, data);
    },
    [socket]
  );

  const chooseFinalResult = React.useCallback(
    async (data: TaskFinalResultArgs) => {
      return await finalResultEmitter(socket, data);
    },
    [socket]
  );

  const handlers = {
    createGame,
    joinGame,
    castVote,
    getVotingSystems,
    getLocalToken,
    reconnectToGame,
    createTimer,
    addTime,
    cancelTime,
    resetTimer,
    updateUser,
    revealVotes,
    startNewVoting,
    finishGame,
    updateTask,
    addTask,
    removeTask,
    chooseFinalResult,
  };

  React.useEffect(() => {
    registerListeners(socket, dispatch);
    getVotingSystems();
    return () => deregisterListeners(socket);
  }, [socket, getVotingSystems]);

  return (
    <GameContext.Provider value={[gameState, handlers]}>
      {children}
    </GameContext.Provider>
  );
};
